import { type ReactNode, useState } from "react";

import {
  Box,
  CheckIcon,
  Column,
  DeleteIcon,
  EditIcon,
  Menu,
  MenuActionsButton,
  MenuItem,
  MenuList,
  ResetIcon,
  Row,
  Text,
  TextWithTooltip,
  Tooltip,
  IconButton,
  ExternalLinkIcon,
  LinkIcon,
  type MenuItemProps,
  WandIcon,
} from "@hightouch/htui";

import type { MentionNames } from "@hightouch/lib/llm/asset-comments/mention-tokens";
import { AssetCommentStatuses } from "@hightouch/lib/llm/asset-comments/types";

import { CommentAuthorAvatar } from "./comment-author-avatar";
import { CommentAuthorLine } from "./comment-author-line";
import { PendingComment } from "./pending-comment";
import { Link } from "src/router";
import { useIsHightouchStaff } from "src/hooks/use-is-hightouch-staff";
import { useChatComposerContext } from "src/components/llm/context/chat-composer-context";
import { buildCommentAgentPrompt } from "./agent-prompt";
import {
  type CommentDensity,
  MarkdownCommentBody,
} from "./markdown-comment-body";
import { CommentComposer, type CommentDraft } from "./comment-composer";
import type { MentionOption } from "./mention-options-list";
import { UnreadDot } from "./unread-dot";
import { formatCommentAge } from "./utils";

/** Marks the comment a link points at, so its thread card can scroll to it. */
export const LINKED_COMMENT_ATTRIBUTE = "data-linked-comment";

/** An entry in a comment's menu, beyond the ones every comment has. */
export type CommentAction = Pick<MenuItemProps, "icon" | "variant"> & {
  label: string;
  onClick: () => void;
};

/** For a comment that stands for more than itself, such as a review's change request. */
export type CommentMenu = {
  editLabel?: string;
  /** Take the place of deleting the comment. */
  actions: [CommentAction, ...CommentAction[]];
};

export type CommentAuthor =
  | { kind: "user"; id: number; name: string }
  | { kind: "reviewAgent"; id: string; name: string }
  | { kind: "agent"; name: string };

export type CommentItemProps = {
  author: CommentAuthor;
  body: string | null;
  /** Current names of this comment's mention targets, for highlighting them. */
  mentionNames: MentionNames;
  createdAt: string;
  status: string;
  /** The run filling in a pending agent reply; null on every other comment. */
  interactionId?: string | null;
  /** The run's chat session, for the staff-only link to it. */
  sessionId?: string | null;
  timestampLabel?: string;
  subtitle?: string | null;
  badge?: ReactNode;
  /** Only the author may edit or delete, mirroring the service-layer rule. */
  canModify: boolean;
  onDelete?: () => void;
  /**
   * What the delete action removes. The rail's only comment *is* the thread, so
   * deleting there removes all of it; inline, a reply is just a reply.
   */
  deleteLabel?: string;
  /** Rendered on the first comment of a thread only. */
  onResolve?: () => void;
  isResolved?: boolean;
  /**
   * Whether the viewer has unread comments in this thread. Marked outside the
   * actions, which only appear on hover.
   */
  isUnread?: boolean;
  /** Marks read or unread by hand. Opening a thread already marks it read. */
  onToggleRead?: () => void;
  /** Horizontal inset — the rail is denser than the inline card. */
  paddingX?: number;
  /** Compact tightens the comment's own spacing and its body's. */
  density?: CommentDensity;
  /** The comment a link points at, behind a soft background. */
  isHighlighted?: boolean;
  /** Copies a link to this comment, from its menu. */
  onCopyLink?: () => void;
  /**
   * Rewriting the comment in place, on surfaces that offer it: the inline card
   * does, the rail — a summary of the opening comment — doesn't.
   */
  edit?: {
    searchMentions: (query: string) => Promise<MentionOption[]>;
    /** Resolves to whether it saved; the box stays open when it didn't. */
    onSave: (draft: CommentDraft) => Promise<boolean>;
    isSaving?: boolean;
  };
  menu?: CommentMenu;
};

/**
 * One comment: author, relative time, body, and the author-only actions.
 *
 * An agent reply with no body yet is still shown — it's the `pending` row the
 * backend writes when an agent is tagged, and hiding it would make tagging look
 * like nothing happened.
 */
export const CommentItem = ({
  author,
  body,
  mentionNames,
  createdAt,
  status,
  interactionId = null,
  sessionId = null,
  timestampLabel,
  subtitle,
  badge,
  canModify,
  onDelete,
  deleteLabel = "Delete comment",
  onResolve,
  isResolved,
  isUnread = false,
  onToggleRead,
  paddingX = 3,
  density = "default",
  isHighlighted = false,
  onCopyLink,
  edit,
  menu,
}: CommentItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const isHightouchStaff = useIsHightouchStaff();
  const { isComposerAvailable, appendToComposer } = useChatComposerContext();

  const isPending = status === AssetCommentStatuses.Pending;
  const hasFailed = status === AssetCommentStatuses.Failed;

  // A pending or failed agent reply has no body to rewrite.
  const canEdit = Boolean(edit) && canModify && !isPending && !hasFailed;

  // Anyone may hand any comment to the agent — acting on a note isn't the
  // author's privilege, unlike editing it. Needs a body to hand over, and a
  // chat to hand it to.
  const canSendToAgent = isComposerAvailable && Boolean(body?.trim());

  const saveEdit = async (draft: CommentDraft) => {
    if (await edit?.onSave(draft)) {
      setIsEditing(false);
    }
  };

  const actionsNode = (
    <Row align="center" gap={0.5} flexShrink={0}>
      {/* The box carries its own save and discard; the hover actions would
          only offer ways to lose what's being typed. */}
      <Row
        align="center"
        gap={0.5}
        display={isEditing ? "none" : "flex"}
        opacity={density === "compact" ? 1 : 0}
        _groupHover={{ opacity: 1 }}
        _groupFocusWithin={{ opacity: 1 }}
        transitionProperty="opacity"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        onClick={(event) => event.stopPropagation()}
      >
        {/* First in the bar: the one action that does something to the ad
            rather than to the comment. */}
        {canSendToAgent && body && (
          <Tooltip message="Add to chat">
            <IconButton
              aria-label="Add to chat"
              icon={WandIcon}
              size="sm"
              onClick={() =>
                appendToComposer(
                  buildCommentAgentPrompt({ author, body, mentionNames }),
                )
              }
            />
          </Tooltip>
        )}

        {/* Left of the menu, so the staff affordance reads as one of the
            comment's actions rather than part of its byline. */}
        {isHightouchStaff && sessionId && (
          <Tooltip message="View session (Hightouch admins only)">
            {/* A link rather than an IconButton, which takes no href.
                Sized and centered to sit on the same line as the buttons
                beside it. */}
            <Row
              as={Link}
              href={`/agents/chats/${sessionId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View session"
              align="center"
              justify="center"
              boxSize={6}
              flexShrink={0}
              borderRadius="md"
              color="text.tertiary"
              _hover={{ color: "text.secondary", bg: "base.background" }}
            >
              <Box as={ExternalLinkIcon} boxSize={4} />
            </Row>
          </Tooltip>
        )}

        {(canModify || onToggleRead || onCopyLink || menu) && (
          // Fixed: inside a popover that follows a pin, an absolute hidden list can make the page scroll.
          <Menu computePositionOnMount={false} strategy="fixed">
            <MenuActionsButton size="sm" />
            <MenuList>
              {/* Read state is the viewer's own, so this shows on threads
                  they can't otherwise act on. */}
              {onToggleRead && (
                <MenuItem onClick={onToggleRead}>
                  {isUnread ? "Mark as read" : "Mark as unread"}
                </MenuItem>
              )}
              {onCopyLink && (
                <MenuItem icon={LinkIcon} onClick={onCopyLink}>
                  Copy link
                </MenuItem>
              )}
              {canEdit && (
                <MenuItem icon={EditIcon} onClick={() => setIsEditing(true)}>
                  {menu?.editLabel ?? "Edit comment"}
                </MenuItem>
              )}
              {menu
                ? menu.actions.map((action) => (
                    <MenuItem
                      key={action.label}
                      icon={action.icon}
                      variant={action.variant}
                      onClick={action.onClick}
                    >
                      {action.label}
                    </MenuItem>
                  ))
                : canModify &&
                  onDelete && (
                    <MenuItem
                      icon={DeleteIcon}
                      variant="danger"
                      onClick={onDelete}
                    >
                      {deleteLabel}
                    </MenuItem>
                  )}
            </MenuList>
          </Menu>
        )}

        {onResolve && (
          <Tooltip message={isResolved ? "Reopen" : "Mark as resolved"}>
            <IconButton
              aria-label={isResolved ? "Reopen thread" : "Mark as resolved"}
              icon={isResolved ? ResetIcon : CheckIcon}
              size="sm"
              onClick={onResolve}
            />
          </Tooltip>
        )}
      </Row>

      {isUnread && <UnreadDot inset />}
    </Row>
  );

  const bodyNode = (
    <>
      {isEditing && edit ? (
        <CommentComposer
          placeholder="Comment…"
          searchMentions={edit.searchMentions}
          onSubmit={(draft) => void saveEdit(draft)}
          isSubmitting={edit.isSaving}
          borderRadius="xl"
          edit={{
            initialBody: body ?? "",
            mentionNames,
            onCancel: () => setIsEditing(false),
          }}
        />
      ) : isPending ? (
        <PendingComment interactionId={interactionId} />
      ) : hasFailed ? (
        <Text size="sm" color="text.danger">
          {author.kind === "reviewAgent"
            ? "The review couldn't be completed."
            : `${author.name} couldn't reply. Mention it again to retry.`}
        </Text>
      ) : author.kind !== "user" ? (
        <MarkdownCommentBody body={body ?? ""} density={density} />
      ) : (
        <MarkdownCommentBody
          body={body ?? ""}
          mentionNames={mentionNames}
          hasHardLineBreaks
          density={density}
        />
      )}
    </>
  );

  const resolvedMark = isResolved && (
    <Tooltip message="Resolved">
      <Box
        as={CheckIcon}
        boxSize={4}
        flexShrink={0}
        color="icon.success"
        aria-label="Resolved"
      />
    </Tooltip>
  );

  if (density === "compact") {
    return (
      <Row
        align="flex-start"
        gap={2}
        p={2}
        className="group"
        bg={isHighlighted ? "warning.background" : undefined}
        borderRadius="2xl"
        {...(isHighlighted && { [LINKED_COMMENT_ATTRIBUTE]: "" })}
      >
        <Box flexShrink={0}>
          <CommentAuthorAvatar author={author} size="md" />
        </Box>
        <Column flex={1} minWidth={0}>
          <Row align="center" gap={1.5} minWidth={0}>
            <TextWithTooltip fontWeight="medium" color="text.primary">
              {author.name}
            </TextWithTooltip>
            <Text color="text.primary" whiteSpace="nowrap">
              commented
            </Text>
            <Text size="sm" color="text.disabled" whiteSpace="nowrap">
              {formatCommentAge(createdAt)}
            </Text>
            {badge}
            {resolvedMark}
          </Row>
          {bodyNode}
        </Column>
        {actionsNode}
      </Row>
    );
  }

  return (
    <Column
      gap={1}
      className="group"
      // Highlighted, the background reaches past the text by the same amount it gains in padding, so nothing moves.
      mx={isHighlighted ? -2 : undefined}
      px={isHighlighted ? paddingX + 2 : paddingX}
      py={2}
      bg={isHighlighted ? "warning.background" : undefined}
      borderRadius="lg"
      {...(isHighlighted && { [LINKED_COMMENT_ATTRIBUTE]: "" })}
    >
      <Row align="center" justify="space-between" gap={2}>
        <Row align="center" gap={2} minWidth={0}>
          <CommentAuthorLine
            author={author}
            createdAt={createdAt}
            timestampLabel={timestampLabel}
            subtitle={subtitle}
            badge={badge}
          />

          {/* The rail's only marker that a thread is settled — its cards show
              just the opening comment, so without this a resolved thread reads
              the same as an open one. */}
          {resolvedMark}
        </Row>

        {actionsNode}
      </Row>

      {bodyNode}
    </Column>
  );
};
